/* eslint-disable max-lines-per-function */
import { createApp, reactive } from 'vue';
import type { ChatOptions, ChatInstance, ChatComponentInstance } from '@chatbot/typings/types';
import { ChatPlugin } from '@chatbot/plugins/chat';
import { addStyleInjectionMixin } from '@chatbot/utils/component-styles';
import Chat from './Chat.ce.vue';
import {
    resolveOptions,
    resolveMountingTarget,
    mergeOptionGroups,
    updateDirectOptions,
} from '@chatbot/utils/chat-options';
import { type Composer, createI18n } from 'vue-i18n';

import en from '@chatbot/translations/generated-files/locales/en.json';
import nl from '@chatbot/translations/generated-files/locales/nl.json';

const setupI18n = (): ReturnType<typeof createI18n> => createI18n({
    legacy: false,
    locale: 'nl',
    globalInjection: false,
    fallbackLocale: 'en',
    messages: {
        nl,
        en,
    },
});

const createChatInstance = (
    app: ReturnType<typeof createApp>,
    appInstance: ChatComponentInstance,
    targetElement: HTMLElement,
    reactiveOptions: ChatOptions,
    i18nInstance: ReturnType<typeof setupI18n>,
): ChatInstance => ({
    updateOptions(newOptions: Partial<ChatOptions>) {
        mergeOptionGroups(newOptions, reactiveOptions);
        updateDirectOptions(newOptions, reactiveOptions);
    },
    destroy() {
        app.unmount();
        if (targetElement.parentNode) {
            targetElement.parentNode.removeChild(targetElement);
        }
    },
    getMessages() {
        return appInstance.messages ?? [];
    },
    clearMessages() {
        appInstance.handleNewConversation();
    },
    sendMessage(text: string) {
        appInstance.handleSendMessage(text);
    },
    newConversation() {
        appInstance.handleNewConversation();
    },
    async sendPendingMessage(message: string) {
        return appInstance.sendPendingMessage(message);
    },
    setLocale(locale: string) {
        if (i18nInstance.mode === 'legacy') {
            i18nInstance.global.locale = locale;
        } else {
            ((i18nInstance.global as unknown) as Composer).locale.value = locale;
        }
    },
});

export const createChat = (options?: Partial<ChatOptions>): ChatInstance => {
    const resolvedOptions = resolveOptions(options);
    const reactiveOptions = reactive<ChatOptions>(resolvedOptions);
    const targetElement = resolveMountingTarget(resolvedOptions);
    const app = createApp(Chat);

    app.use(ChatPlugin, reactiveOptions);

    // Inject component styles into the DOM (needed for .ce.vue files)
    addStyleInjectionMixin(app);

    const i18n = setupI18n();

    app.use(i18n);

    const appInstance = app.mount(targetElement) as unknown as ChatComponentInstance;

    return createChatInstance(
        app, appInstance, targetElement, reactiveOptions, i18n,
    );
};
